import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Table, Space, Button, message, Dropdown, Menu, Modal, Tag } from 'antd';
import { FaEllipsisV } from 'react-icons/fa';
import { api, API_BASE_URL } from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { useMyContext } from "../../context/MyContext";
import Loader from '../shared/loader/loader';
import AskForEstimate from '../shared/askForQuestion/EstimateMessageComponent';

const Estimations = ({ searchQuery, filterCondition }) => {
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [currentEstimateId, setCurrentEstimateId] = useState(null);
  const { user, notifications } = useMyContext();
  const navigate = useNavigate();

  const { data: estimatesData, isLoading, isError, refetch } = useQuery(
    ['estimates', filterCondition],
    async () => {
      const res = await api.get(`${API_BASE_URL}/clientestimates`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const deleteMutation = useMutation(
    async (estimateId) => {
      if (!estimateId) return;
      await api.delete(`${API_BASE_URL}/clientestimates/id/${estimateId}`);
    },
    {
      onSuccess: () => {
        message.success("Estimate deleted successfully!");
        refetch();
      },
      onError: (error) => {
        message.error(`Could not delete estimate: ${error.message}`);
      },
    }
  );

  const handleCommentClick = async (id, estimateId) => {
    setCurrentEstimateId({ id, estimateId });
    setShowCommentModal(true);
  };

  const handleMenuClick = ({ key, item }) => {
    if (key === "create") {
      handleCreateEstimate(item);
    } else if (key === "delete") {
      setSelectedEstimate(item);
      setShowDeleteConfirmationModal(true);
    } else if (key === "comment") {
      handleCommentClick(item._id, item.estimateId);
    }
  };

  const handleCreateEstimate = (record) => {
    const source = filterCondition.estimationAccept ? 'estimates' : 'proposals';
    navigate(source === 'proposals'
      ? `/estimated-ior/${record.estimateId}`
      : `/complete-estimation/${record.estimateId}`
    );
  };

  const handleDelete = () => {
    deleteMutation.mutate(selectedEstimate.estimateId);
    setShowDeleteConfirmationModal(false);
  };

  const columns = [
    {
      title: 'Proposal Id',
      dataIndex: 'estimateId',
      key: '_id',
    },
    {
      title: 'Name',
      dataIndex: 'userName',
      key: 'userName',
    },
    {
      title: 'Origin Country',
      dataIndex: 'originCountry',
      key: 'originCountry',
    },
    {
      title: 'Destination Country',
      dataIndex: 'destinationCountry',
      key: 'destinationCountry',
    },
    {
      title: 'Currency',
      dataIndex: 'itemsCurrency',
      key: 'itemsCurrency',
    },
    {
      title: 'Weight Unit',
      key: 'weightUnit',
      render: (_, record) => {
        const dimensions = record.dimensions;
        if (dimensions && dimensions.length > 0 && dimensions[0].weight && dimensions[0].weight.unit) {
          return dimensions[0].weight.unit;
        }
        return 'N/A';
      },
    },
    {
      title: 'Selected Service',
      dataIndex: 'selectedService',
      key: 'selectedService',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        if (record.isDeclined) {
          return <Tag color="red">Declined</Tag>;
        } else if (record.estimationAccept) {
          return <Tag color="green">Accepted</Tag>;
        } else {
          return <Tag color="gold">Pending</Tag>;
        }
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        const relevantLink = filterCondition.estimationAccept
          ? `/complete-estimation/${record.estimateId}`
          : `/estimated-ior/${record.estimateId}`;
        const hasUnreadNotification = notifications.some(
          (n) =>
            (n.quoteNumber === String(record.estimateId) && !n.read) ||
            (n.link === relevantLink && !n.read)
        );

        // Determine button text based on filterCondition
        const actionButtonText = filterCondition.estimationAccept === false
          ? 'View Estimation'
          : 'Update Estimation';

        return (
          <Space size="middle">
            <Dropdown
              overlay={
                <Menu onClick={(e) => handleMenuClick({ key: e.key, item: record })}>
                  <Menu.Item key="create">{actionButtonText}</Menu.Item>
                  <Menu.Item key="delete">Delete</Menu.Item>
                  <Menu.Item
                    key="comment"
                    onClick={() => {
                      handleCommentClick(record._id, record.estimateId);
                    }}
                  >
                    <Space>Comment</Space>
                  </Menu.Item>
                </Menu>
              }
              trigger={['click']}
            >
              <div className="relative inline-block">
                <Button icon={<FaEllipsisV />} />
                {hasUnreadNotification && (
                  <div className="w-2 h-2 absolute -top-[2px] right-0 bg-red-600 rounded-full"></div>
                )}
              </div>
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  const filteredData = estimatesData
    ?.filter((estimate) =>
      estimate.estimationAccept === filterCondition.estimationAccept &&
      Object.values(estimate).some(
        (value) =>
          typeof value === 'string' &&
          value.toLowerCase().includes(searchQuery.toLowerCase())
      )
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  if (isError) return <div>Error fetching estimates</div>;

  return (
    <div className="mt-4 p-3 bg-white rounded-md md:p-5 gap-y-4 md:flex-row">
      <Modal
        title="Confirm Delete"
        visible={showDeleteConfirmationModal}
        onOk={handleDelete}
        onCancel={() => setShowDeleteConfirmationModal(false)}
        okText="Yes"
        cancelText="No"
      >
        <p>Are you sure you want to delete this estimate?</p>
      </Modal>
      {isLoading ? <Loader /> :
        <Table columns={columns} dataSource={filteredData} rowKey="_id" />
      }
      <Modal
        title={
          <div className="flex flex-col">
            <span>Comments</span>
            <span className="text-xs opacity-60">
              Proposal ID: {currentEstimateId?.estimateId ?? ""}
            </span>
          </div>
        }
        open={showCommentModal}
        onCancel={() => setShowCommentModal(false)}
        footer={null}
        width={"70%"}
        styles={{ body: { padding: 0 } }}
      >
        {currentEstimateId && (
          <AskForEstimate
            user={user}
            id={currentEstimateId.id}
            estimateId={currentEstimateId.estimateId}
            proposalId={currentEstimateId.estimateId}
          />
        )}
      </Modal>
    </div>
  );
};

export default Estimations;